import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TZ_OPTIONS } from "@/pages/Properties";
import { PROPERTY_COLORS, propColor } from "@/lib/propertyColors";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { MonthlyFinancials } from "@/components/MonthlyFinancials";
import { ArrowLeft, Loader2, Save, Building2, Landmark, ClipboardList, SlidersHorizontal, LineChart, Wand2, ShieldCheck, Trash2, Plus } from "lucide-react";
import { LicensesPanel } from "@/components/LicensesPanel";
import { HkMissingRoomsToggle } from "@/components/HkMissingRoomsToggle";
import { ReviewLinksSection } from "@/components/ReviewLinksSection";
import { PropertyNotes } from "@/components/PropertyNotes";
import { BankAccountsEditor } from "@/components/BankAccounts";
import { NotebookPen } from "lucide-react";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const uploadLicense = searchParams.get("upload") || null;
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [reports, setReports] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newShift, setNewShift] = useState("");
  const isAdmin = can(user, ["admin"]);
  const [propUsers, setPropUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users")
      .then((r) => setPropUsers((r.data || []).filter((u) => (u.property_ids || []).includes(id) || ["admin", "regional"].includes(u.role))))
      .catch(() => setPropUsers([]));
  }, [id, isAdmin]);
  const toggleReminderUser = (uid) => {
    const cur = prop?.daily_report_reminder_user_ids || [];
    setF("daily_report_reminder_user_ids", cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };
  const [newCardLabel, setNewCardLabel] = useState("");
  const [newCardLast4, setNewCardLast4] = useState("");
  const [newCardStmt, setNewCardStmt] = useState("");
  const addCard = async () => {
    if (newCardLast4.replace(/\D/g, "").length < 4) { toast.error("Enter the last 4 digits"); return; }
    try {
      const r = await api.post(`/properties/${id}/cards`, { label: newCardLabel, last4: newCardLast4, statement_day: Number(newCardStmt) || 0 });
      setProp((p) => ({ ...p, credit_cards: [...(p.credit_cards || []), r.data] }));
      setNewCardLabel(""); setNewCardLast4(""); setNewCardStmt("");
      toast.success("Card added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateCardStmt = async (cardId, day) => {
    const d = Math.max(0, Math.min(31, Number(day) || 0));
    setProp((p) => ({ ...p, credit_cards: (p.credit_cards || []).map((c) => c.id === cardId ? { ...c, statement_day: d } : c) }));
    try { await api.patch(`/properties/${id}/cards/${cardId}`, { statement_day: d }); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderLog, setReminderLog] = useState([]);
  const loadReminderLog = useCallback(() => {
    if (!can(user, ["admin"])) return;
    api.get("/daily-reports/reminder-log", { params: { property_id: id } }).then((r) => setReminderLog(r.data.items || [])).catch(() => {});
  }, [id, user]);
  useEffect(() => { loadReminderLog(); }, [loadReminderLog]);
  const runReminders = async () => {
    setReminderBusy(true);
    try {
      const r = await api.post("/daily-reports/run-reminders", null, { params: { force: true } });
      if (!r.data.mail_configured) {
        toast.error("Email isn't configured here — reminders show in-app but won't email until Resend is set up in production.");
      } else if (r.data.emails_sent > 0) {
        toast.success(`Sent ${r.data.emails_sent} reminder email(s)`);
      } else {
        toast.message("Nothing to send — reports are submitted, or no responsible party is set.");
      }
      loadReminderLog();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReminderBusy(false); }
  };
  const removeCard = async (cardId) => {
    try {
      await api.delete(`/properties/${id}/cards/${cardId}`);
      setProp((p) => ({ ...p, credit_cards: (p.credit_cards || []).filter((c) => c.id !== cardId) }));
      toast.success("Card removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shortT = (hhmm) => {
    if (!hhmm) return "";
    const [H, M] = hhmm.split(":").map(Number);
    const ap = H >= 12 ? "p" : "a";
    const h12 = H % 12 === 0 ? 12 : H % 12;
    return M === 0 ? `${h12}${ap}` : `${h12}:${String(M).padStart(2, "0")}${ap}`;
  };
  const plus8 = (hhmm) => {
    const [H, M] = hhmm.split(":").map(Number);
    const t = (H * 60 + M + 480) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  };
  const addShiftStart = (t) => {
    if (!t) return;
    const cur = prop.shift_start_times || [];
    if (cur.includes(t)) return;
    setF("shift_start_times", [...cur, t].sort());
  };
  const removeShiftStart = (t) => setF("shift_start_times", (prop.shift_start_times || []).filter((x) => x !== t));
  const generateRotating = (base) => {
    if (!base) return;
    setF("shift_start_times", [base, plus8(base), plus8(plus8(base))].sort());
  };

  const canEdit = can(user, ["admin", "regional"]);
  const canBank = can(user, ["admin", "regional", "finance"]);
  const canNotes = can(user, ["admin", "regional", "accountant", "finance", "gm"]);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProp(r.data)).catch(() => navigate("/properties"));
    api.get(`/daily-reports?property_id=${id}`).then((r) => setReports(r.data)).catch(() => {});
    api.get("/grid-config").then((r) => setGridColumns(r.data.columns)).catch(() => {});
  }, [id, navigate]);

  const setF = (k, v) => setProp((p) => ({ ...p, [k]: v }));
  const setBank = (k, v) => setProp((p) => ({ ...p, bank: { ...(p.bank || {}), [k]: v } }));
  const toggleColumn = (key) => setProp((p) => {
    const hidden = new Set(p.hidden_columns || []);
    if (hidden.has(key)) hidden.delete(key); else hidden.add(key);
    return { ...p, hidden_columns: [...hidden] };
  });

  // ---- Monthly financials (history for Owner Dashboard) ----
  const canFinance = can(user, ["admin", "regional", "finance", "gm"]);

  const [geoBusy, setGeoBusy] = useState(false);
  const geocode = async () => {
    setGeoBusy(true);
    try {
      const { data } = await api.post(`/properties/${id}/geocode`);
      setProp((p) => ({ ...p, geo_lat: data.geo_lat, geo_lng: data.geo_lng }));
      toast.success(`Located: ${data.matched}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGeoBusy(false);
    }
  };
  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("This device can't share location");
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setProp((p) => ({ ...p, geo_lat: pos.coords.latitude, geo_lng: pos.coords.longitude }));
        setGeoBusy(false);
        toast.success("Captured this device's location — remember to Save");
      },
      () => { setGeoBusy(false); toast.error("Couldn't get location (permission denied?)"); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id: _id, created_at, ...body } = prop;
      body.rooms = Number(body.rooms) || 0;
      body.bank = {
        ...body.bank,
        starting_check_number: Number(body.bank?.starting_check_number) || 1001,
        current_check_number: Number(body.bank?.current_check_number) || 1001,
      };
      const { data } = await api.patch(`/properties/${id}`, body);
      setProp(data);
      toast.success("Property saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!prop) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const bank = prop.bank || {};

  return (
    <div className="space-y-8" data-testid="property-detail-page">
      <div>
        <button onClick={() => navigate("/properties")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to properties
        </button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="h-12 w-1.5 rounded-full" style={{ backgroundColor: propColor(prop) }} data-testid="detail-color-accent" />
            <div>
              <div className="label-caps" style={{ color: propColor(prop) }}>Property</div>
              <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">{prop.name}</h1>
              {[prop.brand, [prop.city, prop.state].filter(Boolean).join(", ")].filter(Boolean).length > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {[prop.brand, [prop.city, prop.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>
          {(canEdit || canBank) && (
            <Button onClick={save} disabled={saving} data-testid="save-detail-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Changes
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="overview" data-testid="tab-overview"><Building2 className="mr-2 h-4 w-4" />Overview</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank"><Landmark className="mr-2 h-4 w-4" />Bank & Checks</TabsTrigger>
          <TabsTrigger value="fields" data-testid="tab-fields"><SlidersHorizontal className="mr-2 h-4 w-4" />Daily Report Fields</TabsTrigger>
          <TabsTrigger value="financials" data-testid="tab-financials"><LineChart className="mr-2 h-4 w-4" />Financials</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><ClipboardList className="mr-2 h-4 w-4" />Reports</TabsTrigger>
          <TabsTrigger value="licenses" data-testid="tab-licenses"><ShieldCheck className="mr-2 h-4 w-4" />Licenses &amp; Permits</TabsTrigger>
          {canNotes && <TabsTrigger value="notes" data-testid="tab-notes"><NotebookPen className="mr-2 h-4 w-4" />Notes</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Card className="rounded-none border p-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {[
                ["name", "Property Name"], ["dba_name", "DBA Name"], ["llc_name", "LLC Name"],
                ["brand", "Brand / Flag"], ["address", "Address"], ["city", "City"], ["state", "State"],
              ].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input value={prop[k] || ""} disabled={!canEdit} onChange={(e) => setF(k, e.target.value)} data-testid={`detail-${k}`} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label>Rooms</Label>
                <Input type="number" value={prop.rooms || 0} disabled={!canEdit} onChange={(e) => setF("rooms", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Acquisition / Business Start Date</Label>
                <Input type="date" value={prop.business_start_date || ""} disabled={!canEdit} onChange={(e) => setF("business_start_date", e.target.value)} data-testid="detail-business-start-date" />
                <p className="text-xs text-muted-foreground">P&L ignores revenue, bank transactions and CC receipts before this date.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Time Zone</Label>
                <Select value={prop.timezone || "America/New_York"} disabled={!canEdit} onValueChange={(v) => setF("timezone", v)}>
                  <SelectTrigger data-testid="detail-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TZ_OPTIONS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Payroll Frequency</Label>
                <Select value={prop.payroll_frequency || "biweekly"} disabled={!canEdit} onValueChange={(v) => setF("payroll_frequency", v)}>
                  <SelectTrigger data-testid="detail-payroll-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly (7 days)</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly (14 days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Pay Period Start Date <span className="font-normal text-muted-foreground">(anchor)</span></Label>
                <Input type="date" value={prop.payroll_anchor || ""} disabled={!canEdit} onChange={(e) => setF("payroll_anchor", e.target.value)} data-testid="detail-payroll-anchor" />
                <p className="text-xs text-muted-foreground">Pick any day a pay period begins; all future periods are counted from here.</p>
              </div>
              <div className="space-y-2 rounded-lg border border-border p-3 md:col-span-2" data-testid="detail-pto-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold">Accrue PTO from time clock</Label>
                    <p className="text-xs text-muted-foreground">Employees earn PTO based on hours worked, since their hire anniversary.</p>
                  </div>
                  <Switch checked={!!prop.pto_accrual_enabled} disabled={!canEdit}
                    onCheckedChange={(v) => setF("pto_accrual_enabled", v)} data-testid="detail-pto-enabled" />
                </div>
                {prop.pto_accrual_enabled && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                    <span>Earn</span>
                    <Input type="number" step="0.01" min="0" value={prop.pto_earn_hours ?? ""} disabled={!canEdit}
                      onChange={(e) => setF("pto_earn_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-pto-earn" />
                    <span>hrs PTO for every</span>
                    <Input type="number" step="0.01" min="0" value={prop.pto_per_hours ?? ""} disabled={!canEdit}
                      onChange={(e) => setF("pto_per_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-pto-per" />
                    <span>hrs worked · max</span>
                    <Input type="number" step="1" min="0" value={prop.pto_max_hours ?? 40} disabled={!canEdit}
                      onChange={(e) => setF("pto_max_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-pto-max" />
                    <span>hrs accrued</span>
                  </div>
                )}
              </div>
              <div className="space-y-2 rounded-lg border border-border p-3 md:col-span-2" data-testid="detail-sick-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold">Accrue Sick Leave (separate bucket)</Label>
                    <p className="text-xs text-muted-foreground">State sick-leave earned from hours worked, tracked apart from PTO.</p>
                  </div>
                  <Switch checked={!!prop.sick_accrual_enabled} disabled={!canEdit}
                    onCheckedChange={(v) => setF("sick_accrual_enabled", v)} data-testid="detail-sick-enabled" />
                </div>
                {prop.sick_accrual_enabled && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                    <span>Earn</span>
                    <Input type="number" step="0.01" min="0" value={prop.sick_earn_hours ?? ""} disabled={!canEdit}
                      onChange={(e) => setF("sick_earn_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-sick-earn" />
                    <span>hrs sick for every</span>
                    <Input type="number" step="0.01" min="0" value={prop.sick_per_hours ?? ""} disabled={!canEdit}
                      onChange={(e) => setF("sick_per_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-sick-per" />
                    <span>hrs worked · max</span>
                    <Input type="number" step="1" min="0" value={prop.sick_max_hours ?? 40} disabled={!canEdit}
                      onChange={(e) => setF("sick_max_hours", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="h-9 w-20" data-testid="detail-sick-max" />
                    <span>hrs accrued</span>
                  </div>
                )}
                {prop.sick_accrual_enabled && (
                  <div className="mt-1 space-y-2 border-t border-border pt-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Year-end carryover rules</Label>
                      <Switch checked={!!prop.sick_carryover_enabled} disabled={!canEdit}
                        onCheckedChange={(v) => setF("sick_carryover_enabled", v)} data-testid="detail-sick-carryover-enabled" />
                    </div>
                    {prop.sick_carryover_enabled && (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span>Carry up to</span>
                        <Input type="number" step="1" min="0" value={prop.sick_carryover_max ?? 40} disabled={!canEdit}
                          onChange={(e) => setF("sick_carryover_max", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          className="h-9 w-20" data-testid="detail-sick-carryover-max" />
                        <span>hrs into the new year · use up to</span>
                        <Input type="number" step="1" min="0" value={prop.sick_annual_use_max ?? 0} disabled={!canEdit}
                          onChange={(e) => setF("sick_annual_use_max", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          className="h-9 w-20" data-testid="detail-sick-annual-use" />
                        <span>hrs/yr <span className="text-muted-foreground">(0 = no limit)</span></span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-3 rounded-lg border border-border p-4 md:col-span-2" data-testid="detail-cards-section">
                <div>
                  <Label className="text-sm font-bold">Credit cards</Label>
                  <p className="text-xs text-muted-foreground">Cards used to pay expenses on this property. Staff pick which card when paying by credit card. You can have several.</p>
                </div>
                <div className="space-y-2">
                  {(prop.credit_cards || []).length === 0 && <p className="text-xs text-muted-foreground">No cards added yet.</p>}
                  {(prop.credit_cards || []).map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border border-border px-3 py-2 text-sm" data-testid={`detail-card-${c.id}`}>
                      <span className="font-medium">{c.label} <span className="font-mono text-muted-foreground">••{c.last4}</span></span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Stmt ends day</span>
                        <Input type="number" min="0" max="31" defaultValue={c.statement_day || ""} placeholder="—" disabled={!canEdit}
                          onBlur={(e) => canEdit && Number(e.target.value || 0) !== (c.statement_day || 0) && updateCardStmt(c.id, e.target.value)}
                          className="h-8 w-16" data-testid={`detail-card-stmt-${c.id}`} />
                        {canEdit && <button onClick={() => removeCard(c.id)} className="text-red-500 hover:text-red-600" data-testid={`detail-card-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs">Nickname</Label>
                      <Input value={newCardLabel} onChange={(e) => setNewCardLabel(e.target.value)} placeholder="Amex – Ops" data-testid="detail-card-label" />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Last 4</Label>
                      <Input value={newCardLast4} onChange={(e) => setNewCardLast4(e.target.value)} maxLength={4} inputMode="numeric" placeholder="1009" data-testid="detail-card-last4" />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Stmt ends day</Label>
                      <Input type="number" min="1" max="31" value={newCardStmt} onChange={(e) => setNewCardStmt(e.target.value)} placeholder="e.g. 15" data-testid="detail-card-stmt" />
                    </div>
                    <Button variant="outline" size="sm" onClick={addCard} data-testid="detail-card-add"><Plus className="mr-1 h-4 w-4" /> Add card</Button>
                  </div>
                )}
              </div>

              {isAdmin && (
                <div className="space-y-3 rounded-lg border border-border p-4 md:col-span-2" data-testid="detail-daily-reminder-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-bold">Daily Report reminder</Label>
                      <p className="text-xs text-muted-foreground">Remind when Daily Payroll isn't saved, housekeeper rooms are missing, or the Daily Report isn't Submitted (last 14 days). Admin-only setting.</p>
                    </div>
                    <Switch checked={!!prop.daily_report_reminder_enabled} disabled={!canEdit}
                      onCheckedChange={(v) => setF("daily_report_reminder_enabled", v)} data-testid="detail-reminder-enabled" />
                  </div>
                  {prop.daily_report_reminder_enabled && (
                    <div className="space-y-3 border-t border-border pt-3">
                      <p className="text-sm" data-testid="detail-reminder-schedule">Emails go out at <b>1 PM</b> and <b>3 PM</b> to this property's GMs (plus anyone ticked below), and at <b>5 PM</b> also to Regional managers and Admins — property local time — until Daily Payroll is saved, housekeeper Stayover/Checkout rooms are entered and the Daily Report is Submitted (checks the last 14 days).</p>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">Also remind <span className="font-normal text-muted-foreground">(GMs are always included)</span></Label>
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                          {propUsers.length === 0 && <p className="text-xs text-muted-foreground">No users assigned to this property yet.</p>}
                          {propUsers.map((u) => (
                            <label key={u.id} className="flex items-center gap-2 border border-border px-3 py-2 text-sm" data-testid={`reminder-user-${u.id}`}>
                              <Checkbox checked={(prop.daily_report_reminder_user_ids || []).includes(u.id)} disabled={!canEdit}
                                onCheckedChange={() => toggleReminderUser(u.id)} data-testid={`reminder-user-cb-${u.id}`} />
                              <span className="truncate">{u.name || u.email} <span className="text-xs text-muted-foreground">({u.role})</span></span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">CC email <span className="font-normal text-muted-foreground">(optional)</span></Label>
                        <Input type="email" placeholder="owner@example.com" value={prop.daily_report_reminder_cc || ""} disabled={!canEdit}
                          onChange={(e) => setF("daily_report_reminder_cc", e.target.value)}
                          className="h-9 sm:w-80" data-testid="detail-reminder-cc" />
                      </div>
                      <div className="border-t border-border pt-3">
                        <Button variant="outline" size="sm" onClick={runReminders} disabled={reminderBusy} data-testid="detail-reminder-run">
                          {reminderBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Check &amp; send reminders now
                        </Button>
                        <p className="mt-1 text-xs text-muted-foreground">Save your changes first. Reminders auto-run every 15 min; this sends immediately for testing. Emails require Resend configured in production.</p>
                        {reminderLog.length > 0 && (
                          <div className="mt-3 space-y-1" data-testid="detail-reminder-log">
                            <div className="label-caps text-[0.6rem] text-muted-foreground">Recent reminders sent</div>
                            {reminderLog.slice(0, 5).map((l, i) => (
                              <div key={i} className="text-xs text-muted-foreground">
                                For {l.date}: sent {l.count}× · last {l.last_sent_at ? new Date(l.last_sent_at).toLocaleString() : "—"} · to {l.recipients?.join(", ") || "—"}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <ReviewLinksSection propertyId={id} isAdmin={isAdmin} canEdit={canEdit} reminderOn={prop.review_reply_reminder_enabled}
                onReminderChange={(v) => setF("review_reply_reminder_enabled", v)} />
              {isAdmin && (
                <HkMissingRoomsToggle enabled={prop.hk_missing_rooms_alert_enabled} canEdit={canEdit}
                  onChange={(v) => setF("hk_missing_rooms_alert_enabled", v)} />
              )}

              <div className="space-y-3 rounded-lg border border-border p-4 md:col-span-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold">Require location for phone clock-in</Label>
                    <p className="text-xs text-muted-foreground">Employees clocking in on their own phone must be near the property. Off-site punches are flagged for review (never blocked). A front-desk station device is exempt.</p>
                  </div>
                  <Switch checked={!!prop.geofence_enabled} disabled={!canEdit}
                    onCheckedChange={(v) => setF("geofence_enabled", v)} data-testid="detail-geofence-enabled" />
                </div>
                <div className="flex items-center justify-between border-t border-border pt-4">
                  <div>
                    <Label className="text-sm font-bold">Housekeeping Assignment Sheet</Label>
                    <p className="text-xs text-muted-foreground">Adds an "Assignment" tab in Housekeeping so an Exec Housekeeper can assign rooms to each housekeeper for the day. Optional — leave off to keep the current workflow.</p>
                  </div>
                  <Switch checked={!!prop.hk_assignments_enabled} disabled={!canEdit}
                    onCheckedChange={(v) => setF("hk_assignments_enabled", v)} data-testid="detail-hk-assignments-enabled" />
                </div>
                {prop.geofence_enabled && (
                  <div className="space-y-3 pt-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span>Allowed distance</span>
                      <Input type="number" step="10" min="25" value={prop.geofence_radius_m ?? 150} disabled={!canEdit}
                        onChange={(e) => setF("geofence_radius_m", e.target.value === "" ? "" : parseInt(e.target.value, 10))}
                        className="h-9 w-24" data-testid="detail-geofence-radius" />
                      <span>meters from the property</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={geocode} disabled={!canEdit || geoBusy} data-testid="detail-geocode-btn">
                        {geoBusy ? "Locating…" : "Locate from address"}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={captureLocation} disabled={!canEdit || geoBusy} data-testid="detail-capture-location-btn">
                        Use my current location
                      </Button>
                      {prop.geo_lat != null && prop.geo_lng != null ? (
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="detail-geo-coords">
                          📍 {Number(prop.geo_lat).toFixed(5)}, {Number(prop.geo_lng).toFixed(5)}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 dark:text-amber-400">No location set yet — locate from the address, then Save.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Shift Start Times <span className="font-normal text-muted-foreground">(each is an 8-hour shift; used on the Schedule)</span></Label>
                <div className="flex flex-wrap items-center gap-2" data-testid="detail-shift-times">
                  {(prop.shift_start_times || []).length === 0 && (
                    <span className="text-xs text-muted-foreground">None set — the Schedule will use default shifts (7a, 3p, 11p, 9a).</span>
                  )}
                  {(prop.shift_start_times || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium" data-testid={`shift-chip-${t}`}>
                      {shortT(t)}–{shortT(plus8(t))}
                      {canEdit && (
                        <button onClick={() => removeShiftStart(t)} className="text-muted-foreground hover:text-destructive" data-testid={`shift-remove-${t}`}>×</button>
                      )}
                    </span>
                  ))}
                </div>
                {canEdit && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Input type="time" value={newShift} onChange={(e) => setNewShift(e.target.value)} className="h-9 w-32" data-testid="shift-new-input" />
                    <Button type="button" variant="outline" size="sm" onClick={() => { addShiftStart(newShift); setNewShift(""); }} disabled={!newShift} data-testid="shift-add-btn">Add shift</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { generateRotating(newShift); setNewShift(""); }} disabled={!newShift} data-testid="shift-generate-btn">
                      Generate 3 rotating (start, +8h, +16h)
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">e.g. 06:00 gives a 6a–2p shift. Add 06:00, 14:00, 22:00 for the three 8-hour shifts, or type one and hit "Generate 3 rotating".</p>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Accent Color <span className="font-normal text-muted-foreground">(shown on cards, header & badges)</span></Label>
                <div className="flex flex-wrap items-center gap-2" data-testid="detail-color-picker">
                  {PROPERTY_COLORS.map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setF("color", c.v)}
                      title={c.l}
                      data-testid={`detail-color-${c.v.replace("#", "")}`}
                      className={`h-8 w-8 rounded-full transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-60 ${(prop.color || "") === c.v ? "ring-2 ring-offset-2 ring-offset-background" : ""}`}
                      style={{ backgroundColor: c.v, boxShadow: (prop.color || "") === c.v ? `0 0 0 2px ${c.v}` : undefined }}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Room Numbers <span className="font-normal text-muted-foreground">(used by Housekeeping)</span></Label>
                <Textarea value={prop.room_numbers || ""} disabled={!canEdit} onChange={(e) => setF("room_numbers", e.target.value)} placeholder="e.g. 101-120, 201-220, 305" data-testid="detail-room-numbers" />
                <p className="text-xs text-muted-foreground">Enter lists and ranges (e.g. 101-120, 201-220). Housekeepers pick from these when marking rooms cleaned.</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bank" className="mt-6">
          <BankAccountsEditor propertyId={id} canEdit={canBank} />
        </TabsContent>

        <TabsContent value="fields" className="mt-6">
          <Card className="rounded-none border p-6" data-testid="fields-settings">
            <h3 className="font-heading text-lg font-bold tracking-tight">Daily Report Fields</h3>
            <p className="mb-6 text-sm text-muted-foreground">
              Show or hide fields for this property (e.g., hide F&amp;B fields for a property with no restaurant).
              Changes apply to the Daily Report form, Monthly Report grid, and Excel export. Uncheck to hide.
            </p>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {gridColumns.map((c) => {
                const hidden = (prop.hidden_columns || []).includes(c.key);
                return (
                  <label key={c.key} className="flex items-center gap-2.5 border border-border px-3 py-2 text-sm" data-testid={`field-toggle-${c.key}`}>
                    <Checkbox checked={!hidden} disabled={!canEdit} onCheckedChange={() => toggleColumn(c.key)} data-testid={`field-checkbox-${c.key}`} />
                    <span className={hidden ? "text-muted-foreground line-through" : ""}>{c.label}</span>
                    {c.formula && <Badge variant="secondary" className="ml-auto rounded-none text-[0.6rem]">auto</Badge>}
                  </label>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="financials" className="mt-6">
          <Card className="rounded-none border p-6" data-testid="financials-tab">
            <MonthlyFinancials propertyId={id} canEdit={canFinance} />
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <Card className="rounded-none border">
            <div className="flex items-center justify-between border-b border-border p-5">
              <h3 className="font-heading text-lg font-bold tracking-tight">Daily Reports</h3>
              <Button asChild size="sm" variant="outline">
                <Link to={`/daily-reports/new?property=${id}`}>New Report</Link>
              </Button>
            </div>
            <div className="divide-y divide-border">
              {reports.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No reports yet.</div>}
              {reports.map((r) => (
                <Link key={r.id} to={`/daily-reports/${r.property_id}/${r.report_date}`} className="flex items-center justify-between p-4 hover:bg-secondary">
                  <span className="font-medium">{fmtDate(r.report_date)}</span>
                  <Badge variant={r.status === "submitted" ? "default" : "secondary"} className="rounded-none">{r.status}</Badge>
                </Link>
              ))}
            </div>
          </Card>
        </TabsContent>

        {canNotes && <TabsContent value="notes" className="mt-6"><PropertyNotes propertyId={id} /></TabsContent>}
        <TabsContent value="licenses" className="mt-6">
          <LicensesPanel propertyId={id} user={user} initialUploadType={uploadLicense} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
